import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Globals } from '../../global/globals';
import { AnswerRepository } from '../ports/answer-repository.port';
import { AnswerRow } from '../domain/types';

/** Shape returned by the backend GET answers endpoint. */
interface ExtractionAnswerDto {
  questionId: number;
  value: string;
}

/**
 * HTTP adapter for AnswerRepository. Stores answers per
 * (project, paperToProject, authenticated user) on the backend.
 * projectId is read from the current route snapshot with parent fallback.
 */
@Injectable()
export class HttpAnswersAdapter implements AnswerRepository {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly route = inject(ActivatedRoute);

  private projectId(): string {
    return (
      this.route.snapshot.paramMap.get('projectId') ??
      this.route.parent?.snapshot.paramMap.get('projectId') ??
      ''
    );
  }

  private baseUrl(paperToProjectId: string): string {
    return `${this.globals.backendUri}/projects/${this.projectId()}/data-extraction/${paperToProjectId}`;
  }

  async loadFor(paperToProjectId: string): Promise<AnswerRow[]> {
    const dtos = await firstValueFrom(
      this.http.get<ExtractionAnswerDto[]>(`${this.baseUrl(paperToProjectId)}/answers`),
    );
    return dtos.map((d) => ({ questionId: d.questionId, value: d.value }));
  }

  async submitAll(paperToProjectId: string, rows: AnswerRow[]): Promise<void> {
    const body: ExtractionAnswerDto[] = rows.map((r) => ({
      questionId: r.questionId,
      value: r.value,
    }));
    await firstValueFrom(this.http.post<void>(`${this.baseUrl(paperToProjectId)}/save`, body));
  }
}
